// -----------------------------------------------------------------------------
// Toggle "Atender com IA" no header do Kanban
// (`map-funnel-stage-and-add-ia-toggle`).
//
// Renderiza um botão discreto com ícone estrela + órbita (SVG inline,
// ~16×16, sem animação). Cor cinza quando OFF, esmeralda quando ON.
// Ao desligar com leads em curso em IA, abre modal de confirmação com
// opção "Pausar apenas novos" vs "Pausar tudo".
//
// Visibilidade por default ligada. Escape hatch: setar
// `REACT_APP_IA_USER_TOGGLE_ENABLED=false` no `.env` da build esconde o
// toggle. O backend gateia o efeito real (criação de leads em
// `atendimentoModo='ia'`) via `IA_USER_TOGGLE_ENABLED` — flag frontend é
// só visual.
// -----------------------------------------------------------------------------

import React, { useState } from 'react';
import {
  getMyIaActiveCount,
  patchMyPreferences,
} from '../../services/userPreferences';
import { useUserPreferences } from '../../context/UserPreferencesContext';

const FEATURE_ENABLED =
  (process.env.REACT_APP_IA_USER_TOGGLE_ENABLED || 'true').toLowerCase() !== 'false';

interface StarOrbitIconProps {
  active: boolean;
}

function StarOrbitIcon({ active }: StarOrbitIconProps) {
  const stroke = active ? '#059669' : '#64748b';
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <ellipse
        cx="8"
        cy="8"
        rx="6.5"
        ry="3"
        transform="rotate(-25 8 8)"
        stroke={stroke}
        strokeWidth="0.8"
        fill="none"
        opacity="0.6"
      />
      <polygon
        points="8,3.3 8.9,6.1 11.8,6.1 9.5,7.9 10.4,10.7 8,9 5.6,10.7 6.5,7.9 4.2,6.1 7.1,6.1"
        fill={active ? stroke : 'none'}
        stroke={stroke}
        strokeWidth="0.8"
      />
    </svg>
  );
}

interface ConfirmOffModalProps {
  count: number;
  onCancel: () => void;
  onChoose: (scope: 'new_only' | 'include_inflight') => void;
  pending: boolean;
}

function ConfirmOffModal({ count, onCancel, onChoose, pending }: ConfirmOffModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 24,
          maxWidth: 420,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        }}
      >
        <h4 style={{ margin: '0 0 8px', fontSize: 16 }}>Desligar atendimento por IA?</h4>
        <p style={{ margin: '0 0 16px', color: '#475569', fontSize: 14 }}>
          Você tem <strong>{count}</strong> {count === 1 ? 'lead em curso' : 'leads em curso'} com IA ativa.
          Como quer prosseguir?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            disabled={pending}
            onClick={() => onChoose('new_only')}
            style={{
              padding: '8px 12px',
              border: '1px solid #cbd5e1',
              background: '#fff',
              borderRadius: 6,
              textAlign: 'left',
              cursor: pending ? 'wait' : 'pointer',
            }}
          >
            Pausar apenas novos — leads em curso continuam com IA
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => onChoose('include_inflight')}
            style={{
              padding: '8px 12px',
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              borderRadius: 6,
              textAlign: 'left',
              cursor: pending ? 'wait' : 'pointer',
            }}
          >
            Pausar tudo — IA volta pra manual em todos os {count} leads
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={onCancel}
            style={{
              padding: '6px 10px',
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              cursor: pending ? 'wait' : 'pointer',
              alignSelf: 'flex-end',
              fontSize: 13,
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export function KanbanIaToggle() {
  const ctx = useUserPreferences();
  const enabled = ctx?.aiAutoAttendEnabled ?? null;
  const setEnabled = ctx?.setAiAutoAttendEnabled ?? (() => {});
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ count: number } | null>(null);

  if (!FEATURE_ENABLED || enabled === null) return null;

  const handleToggle = async () => {
    if (loading) return;
    if (!enabled) {
      // OFF → ON: aplica direto, sem modal.
      setLoading(true);
      try {
        await patchMyPreferences({ aiAutoAttendEnabled: true });
        setEnabled(true);
      } catch (err) {
        console.error('[KanbanIaToggle] failed to enable', err);
      } finally {
        setLoading(false);
      }
      return;
    }
    // ON → OFF: consulta contagem; se >0 abre modal, se 0 desliga direto.
    setLoading(true);
    try {
      const count = await getMyIaActiveCount();
      if (count === 0) {
        await patchMyPreferences({ aiAutoAttendEnabled: false, scope: 'new_only' });
        setEnabled(false);
      } else {
        setModal({ count });
      }
    } catch (err) {
      console.error('[KanbanIaToggle] failed to read active count', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModalChoose = async (scope: 'new_only' | 'include_inflight') => {
    setLoading(true);
    try {
      await patchMyPreferences({ aiAutoAttendEnabled: false, scope });
      setEnabled(false);
      setModal(null);
    } catch (err) {
      console.error('[KanbanIaToggle] failed to disable with scope', scope, err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading}
        title={enabled ? 'Atender com IA: ligado' : 'Atender com IA: desligado'}
        aria-pressed={enabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          background: 'transparent',
          border: `1px solid ${enabled ? '#059669' : '#cbd5e1'}`,
          borderRadius: 16,
          fontSize: 12,
          color: enabled ? '#059669' : '#64748b',
          cursor: loading ? 'wait' : 'pointer',
          opacity: loading ? 0.7 : 1,
        }}
      >
        <StarOrbitIcon active={enabled} />
        <span>Atender com IA</span>
      </button>
      {modal && (
        <ConfirmOffModal
          count={modal.count}
          pending={loading}
          onCancel={() => setModal(null)}
          onChoose={handleModalChoose}
        />
      )}
    </>
  );
}
